import React from 'react';
import { FadeInSection } from './ui/FadeInSection';
import { CheckCircleIcon, ShieldIcon } from 'lucide-react';
import { SWIFT_LOGO_URL } from './Logo';
export function HeroSection() {
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-gradient-to-b from-brand-lightBlue to-white" data-id="element-105">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-106">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-107">
          {/* Left Content */}
          <FadeInSection data-id="element-108">
            <div className="max-w-2xl" data-id="element-109">
              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-slate-900 leading-[1.1] tracking-tight mb-6" data-id="element-110">
                Real-Time Insurance Eligibility Verification for Every Specialty
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-111">
                Verify patient coverage in seconds.
                <br data-id="element-112" />
                No contracts. No complicated onboarding.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-113">
                <button className="inline-flex justify-center items-center px-8 py-3.5 border border-transparent text-base font-semibold rounded-lg shadow-sm text-white bg-brand-teal hover:bg-brand-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-teal" data-id="element-114">
                  Start Verifying Free
                </button>
                <a href="#how-it-works" className="inline-flex justify-center items-center px-8 py-3.5 border-2 border-slate-300 text-base font-semibold rounded-lg text-slate-700 hover:border-brand-teal hover:text-brand-teal transition-colors" data-id="element-115">
                  See How It Works
                </a>
              </div>
            </div>
          </FadeInSection>

          {/* Right: App Mockup matching screenshot */}
          <FadeInSection delay={200} className="relative mx-auto w-full max-w-lg lg:max-w-none" data-id="element-116">
            <div className="relative bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.12)] border border-slate-200 overflow-hidden z-10" data-id="element-117">
              {/* Browser Chrome */}
              <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center gap-3" data-id="element-118">
                <div className="flex gap-1.5" data-id="element-119">
                  <div className="w-3 h-3 rounded-full bg-slate-300" data-id="element-120"></div>
                  <div className="w-3 h-3 rounded-full bg-slate-300" data-id="element-121"></div>
                  <div className="w-3 h-3 rounded-full bg-slate-300" data-id="element-122"></div>
                </div>
                <div className="flex-1 flex justify-center" data-id="element-123">
                  <div className="bg-white border border-slate-200 rounded-md px-4 py-1.5 flex items-center gap-2 text-xs text-slate-500 max-w-[220px] w-full justify-center" data-id="element-124">
                    <img src={SWIFT_LOGO_URL} alt="" className="w-4 h-4 object-contain" aria-hidden="true" data-id="element-125" />
                    <span data-id="element-126">enSwift — Eligibility Report</span>
                  </div>
                </div>
              </div>

              {/* App Content */}
              <div className="p-6 md:p-8" data-id="element-127">
                {/* Patient Header */}
                <div className="flex items-start justify-between mb-6" data-id="element-128">
                  <div data-id="element-129">
                    <h3 className="text-xl font-bold text-slate-900" data-id="element-130">
                      Sarah Mitchell
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-131">
                      DOB: 03/15/1985 • ID: XWG-882451-01
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold shrink-0" data-id="element-132">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-133"></span>
                    ACTIVE
                  </div>
                </div>

                {/* Insurance Card */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-5" data-id="element-134">
                  <div className="flex items-center gap-3 mb-4" data-id="element-135">
                    <div className="w-9 h-9 bg-brand-teal/10 rounded-lg flex items-center justify-center" data-id="element-136">
                      <ShieldIcon className="w-5 h-5 text-brand-teal" data-id="element-137" />
                    </div>
                    <div data-id="element-138">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-139">
                        Primary Insurance
                      </p>
                      <p className="text-base font-bold text-slate-900" data-id="element-140">
                        Blue Cross Blue Shield
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-141">
                    <div data-id="element-142">
                      <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1" data-id="element-143">
                        Plan Type
                      </p>
                      <p className="text-sm font-semibold text-slate-800" data-id="element-144">
                        PPO Medical
                      </p>
                    </div>
                    <div data-id="element-145">
                      <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1" data-id="element-146">
                        Network
                      </p>
                      <p className="text-sm font-semibold text-slate-800" data-id="element-147">
                        In-Network
                      </p>
                    </div>
                  </div>
                </div>

                {/* Verification Status */}
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-4" data-id="element-148">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-149" />
                  <div data-id="element-150">
                    <p className="text-sm font-bold text-slate-900" data-id="element-151">
                      Eligibility Verified
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-152">
                      Just now • Full report ready
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </div>
    </section>;
}